import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { Course, Track } from '@juvo/schemas';
import { pickLocale } from '@juvo/utils';
import { store } from '../store.js';
import '../layouts/site-chrome.js';

const tracks: Track[] = [
  'digital-health',
  'ai',
  'technical',
  'health-tech',
  'digital-dentistry',
  'embedded',
];

@customElement('jv-courses-page')
export class JvCoursesPage extends LitElement {
  static styles = css`
    h1 {
      font-size: var(--jv-fs-display);
      margin: 0 0 0.6rem;
    }
    .intro {
      color: var(--jv-color-muted);
      max-width: 40rem;
      margin-bottom: 1.75rem;
    }
    .filters {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin-bottom: 1.75rem;
    }
    input,
    select {
      min-height: 2.75rem;
      border: var(--jv-border-w) solid var(--jv-color-border);
      border-radius: 0;
      padding: 0.4rem 0.95rem;
      background: var(--jv-color-surface);
      font-family: inherit;
    }
    input {
      min-width: min(100%, 18rem);
    }
    .grid {
      display: grid;
      gap: 1rem;
    }
    @media (min-width: 800px) {
      .grid {
        grid-template-columns: repeat(12, 1fr);
      }
      a.card-link {
        grid-column: span 4;
      }
    }
    a.card-link {
      text-decoration: none;
      color: inherit;
    }
    h3 {
      font-family: var(--jv-font-display);
      font-size: 1.2rem;
      margin: 0;
    }
    p {
      margin: 0;
      color: var(--jv-color-muted);
    }
    .meta {
      display: flex;
      gap: 0.4rem;
      flex-wrap: wrap;
    }
  `;

  @state() private q = '';
  @state() private track: Track | '' = '';
  @state() private courses: Course[] = [];

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.reload());
    this.reload();
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  private async reload() {
    this.courses = await store.client.getCourses({
      q: this.q || undefined,
      track: this.track || undefined,
      locale: store.locale,
    });
    this.requestUpdate();
  }

  render() {
    const t = store.t.bind(store);
    return html`
      <jv-page>
        <h1>${t('courses.title')}</h1>
        <p class="intro">${t('courses.intro')}</p>
        <div class="filters">
          <input
            placeholder=${t('courses.search')}
            .value=${this.q}
            @input=${this.onSearch}
          />
          <select .value=${this.track} @change=${this.onTrack}>
            <option value="">${t('courses.allTracks')}</option>
            ${tracks.map((track) => html`<option value=${track}>${track}</option>`)}
          </select>
        </div>
        ${this.courses.length === 0
          ? html`<jv-empty-state heading=${t('courses.empty')}></jv-empty-state>`
          : html`<div class="grid">
              ${this.courses.map(
                (course) => html`
                  <a class="card-link" href="/courses/${course.slug}">
                    <jv-card image=${course.heroImage ?? ''} alt=${pickLocale(course.title, store.locale)}>
                      <div class="meta">
                        <jv-badge>${course.code ?? course.programmeType}</jv-badge>
                        <jv-badge tone="sand">${course.level}</jv-badge>
                      </div>
                      <h3>${pickLocale(course.title, store.locale)}</h3>
                      <p>${pickLocale(course.description, store.locale)}</p>
                      <p>${course.durationWeeks} ${t('course.weeks')} · ${course.location}</p>
                    </jv-card>
                  </a>
                `,
              )}
            </div>`}
      </jv-page>
    `;
  }

  private onSearch = (event: Event) => {
    this.q = (event.target as HTMLInputElement).value;
    this.reload();
  };

  private onTrack = (event: Event) => {
    this.track = (event.target as HTMLSelectElement).value as Track | '';
    this.reload();
  };
}
